/**
 * The original password-based registration form, lifted out of PageSignUp.
 *
 * Gated behind the same REACT_APP_PASSWORD_LOGIN_ENABLED flag as the password
 * login: handing someone a password they would not be allowed to sign in with
 * is worse than not offering one. Kept rather than deleted, for the same reason
 * PasswordLoginForm is.
 */

import React, { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form, FormFeedback } from 'reactstrap';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import Input from 'shared/Input/Input';
import ButtonPrimary from 'shared/Button/ButtonPrimary';
import axios from '../../service/axios';
import { authErrorMessage } from 'service/authApi';

type FormValues = {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    confirmPassword: string;
    mobile?: string;
};

const validationScheme = yup.object().shape({
    firstName: yup.string().trim().required('First name is required.'),
    lastName: yup.string().trim().required('Last name is required.'),
    email: yup.string().email('Must be a valid email.').required('Email is required'),
    password: yup.string().required('Password is required.'),
    confirmPassword: yup
        .string()
        .required('Please confirm your password.')
        .oneOf([yup.ref('password')], 'Passwords do not match.'),
    mobile: yup
        .string()
        .trim()
        .matches(/^(\+?91[-\s]?)?[6-9]\d{9}$/, { message: 'Enter a valid mobile number.', excludeEmptyString: true })
});

export interface PasswordSignUpFormProps {
    className?: string;
}

const PasswordSignUpForm: FC<PasswordSignUpFormProps> = ({ className = '' }) => {
    const navigate = useNavigate();
    const [buttonStatus, setButtonStatus] = useState<boolean>(false);

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm<FormValues>({
        mode: 'onChange',
        resolver: yupResolver(validationScheme)
    });

    const onSubmit: SubmitHandler<FormValues> = async (data) => {
        try {
            setButtonStatus(true);
            const response = await axios.post('/customer', {
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                confirmPassword: data.confirmPassword,
                mobile: data.mobile ? Number(data.mobile) : undefined,
                password: data.password
            });

            if (response?.data) {
                toast.success('Account created successfully. Please login to place order.');
                navigate('/login');
            }
        } catch (error) {
            toast.error(authErrorMessage(error, 'Something went wrong!'));
        } finally {
            setButtonStatus(false);
        }
    };

    return (
        <Form className={`grid grid-cols-1 gap-3 ${className}`} action="#" method="post" onSubmit={handleSubmit(onSubmit)}>
            <label className="block">
                <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200">First Name</span>
                <Input
                    type="text"
                    autoComplete="given-name"
                    className="mt-1"
                    style={{ borderColor: errors.firstName ? '#ea5455' : '' }}
                    {...register('firstName')}
                />
                {errors && errors.firstName && <FormFeedback style={{ color: '#ea5455' }}>{errors.firstName.message}</FormFeedback>}
            </label>
            <label className="block">
                <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200">Last Name</span>
                <Input
                    type="text"
                    autoComplete="family-name"
                    className="mt-1"
                    style={{ borderColor: errors.lastName ? '#ea5455' : '' }}
                    {...register('lastName')}
                />
                {errors && errors.lastName && <FormFeedback style={{ color: '#ea5455' }}>{errors.lastName.message}</FormFeedback>}
            </label>
            <label className="block">
                <span className="text-neutral-800 dark:text-neutral-200">Email address</span>
                <Input
                    type="email"
                    autoComplete="email"
                    placeholder="example@example.com"
                    className="mt-1"
                    style={{ borderColor: errors.email ? '#ea5455' : '' }}
                    {...register('email')}
                />
                {errors && errors.email && <FormFeedback style={{ color: '#ea5455' }}>{errors.email.message}</FormFeedback>}
            </label>
            <label className="block">
                <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200">Password</span>
                <Input
                    type="password"
                    autoComplete="new-password"
                    className="mt-1"
                    style={{ borderColor: errors.password ? '#ea5455' : '' }}
                    {...register('password')}
                />
                {errors && errors.password && <FormFeedback style={{ color: '#ea5455' }}>{errors.password.message}</FormFeedback>}
            </label>
            <label className="block">
                <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200">Confirm Password</span>
                <Input
                    type="password"
                    autoComplete="new-password"
                    className="mt-1"
                    style={{ borderColor: errors.confirmPassword ? '#ea5455' : '' }}
                    {...register('confirmPassword')}
                />
                {errors && errors.confirmPassword && (
                    <FormFeedback style={{ color: '#ea5455' }}>{errors.confirmPassword.message}</FormFeedback>
                )}
            </label>
            <label className="block">
                <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200">Mobile</span>
                <Input
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    className="mt-1"
                    style={{ borderColor: errors.mobile ? '#ea5455' : '' }}
                    {...register('mobile')}
                />
                {errors && errors.mobile && <FormFeedback style={{ color: '#ea5455' }}>{errors.mobile.message}</FormFeedback>}
            </label>
            <ButtonPrimary type="submit" disabled={buttonStatus} loading={buttonStatus}>
                Continue
            </ButtonPrimary>
        </Form>
    );
};

export default PasswordSignUpForm;
